import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { apiService } from '../../services/api';
import MobileReceiptUploader from './MobileReceiptUploader';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  Paper,
  Tabs,
  Tab,
  Fab,
  SpeedDial,
  SpeedDialAction,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Upload as UploadIcon,
  Warning as WarningIcon,
  AccountBalance as BankIcon,
  AttachMoney as MoneyIcon,
  ArrowBack as BackIcon,
  Menu as MenuIcon
} from '@mui/icons-material';

const MobileExpenseReportForm = () => {
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState('items'); // 'items', 'upload', 'missing', 'bank'
  const [expenseItems, setExpenseItems] = useState([]);
  const [missingReceipts, setMissingReceipts] = useState([]);
  const [bankStatement, setBankStatement] = useState(null);
  const [report, setReport] = useState(null);
  const [canTagWebPortal, setCanTagWebPortal] = useState(false);
  const [error, setError] = useState(null);
  const [speedDialOpen, setSpeedDialOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    fetchExpenseData();
  }, []);

  const fetchExpenseData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const today = new Date();
      const currentMonth = today.getMonth() + 1;
      const currentYear = today.getFullYear();
      
      const response = await apiService.getOrCreateMonthlyReport({
        month: currentMonth,
        year: currentYear,
        create_new: true
      });
      
      setReport(response);
      setExpenseItems(response?.items || []);
      setMissingReceipts(response?.mrlItems || []);
      setBankStatement(response?.bankStatement || null);
      setCanTagWebPortal(Boolean(response?.can_tag_web_portal));
      
    } catch (err) {
      setError('Failed to load expense data');
      console.error('Error fetching expense data:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = () => {
    return expenseItems.reduce((sum, item) => sum + (item?.amount || 0), 0);
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  const formatAmount = (amount) => {
    const numAmount = Number(amount) || 0;
    return numAmount.toFixed(2);
  };

  const speedDialActions = [
    { icon: <UploadIcon />, name: 'Upload Receipt', key: 'upload' },
    { icon: <WarningIcon />, name: `Missing (${missingReceipts.length})`, key: 'missing' },
    { icon: <BankIcon />, name: 'Bank Statement', key: 'bank' }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const renderExpenseItems = () => (
    <Box>
      <AppBar position="static" sx={{ mb: 2 }}>
        <Toolbar>
          <ReceiptIcon sx={{ mr: 2 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="div">
              Expense Items ({expenseItems.length})
            </Typography>
            <Typography variant="body2">
              Total: ${formatAmount(calculateTotal())}
            </Typography>
          </Box>
        </Toolbar>
        
        {/* Header Navigation Tabs */}
        <Tabs
          value={currentView}
          onChange={(e, newValue) => setCurrentView(newValue)}
          variant="fullWidth"
          textColor="inherit"
          indicatorColor="secondary"
          sx={{ bgcolor: 'primary.dark' }}
        >
          <Tab label="Items" value="items" />
          <Tab label="Upload" value="upload" />
          <Tab label={`Missing (${missingReceipts.length})`} value="missing" />
          <Tab label="Bank" value="bank" />
        </Tabs>
      </AppBar>

      {expenseItems.length === 0 ? (
        <Card sx={{ m: 2 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <MoneyIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              No expense items yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload receipts to create expense items
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <List>
          {expenseItems.map((item, index) => (
            <Card key={item?.id || index} sx={{ m: 1, mb: 2 }}>
              <ListItem>
                <ListItemIcon>
                  <MoneyIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={item?.description || 'No description'}
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        ${formatAmount(item?.amount)} • {formatDate(item?.date)}
                      </Typography>
                      {item?.work_order && (
                        <Typography variant="caption" color="text.secondary">
                          Work Order: {item.work_order}
                        </Typography>
                      )}
                      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Chip
                          size="small"
                          label={item?.is_web_portal ? 'Web Portal' : (item?.status || 'ready')}
                          color={item?.is_web_portal ? 'primary' : (item?.status === 'approved' ? 'success' : 'default')}
                          variant="outlined"
                        />
                        {canTagWebPortal && report?.report?.id && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={Boolean(item?.is_web_portal)}
                                onChange={async (e) => {
                                  try {
                                    const updated = await apiService.updateExpenseItem(report.report.id, item.id, {
                                      work_order: item.work_order,
                                      date: typeof item.date === 'string' && item.date.includes('T')
                                        ? item.date.split('T')[0]
                                        : item.date,
                                      description: item.description,
                                      gl_account: item.gl_account,
                                      amount: item.amount,
                                      is_web_portal: e.target.checked
                                    });
                                    setExpenseItems((current) =>
                                      current.map((row) => (row.id === updated.id ? updated : row))
                                    );
                                  } catch (err) {
                                    setError('Failed to update Web Portal tag');
                                    console.error('Error updating Web Portal tag:', err);
                                  }
                                }}
                              />
                            }
                            label="Web Portal"
                          />
                        )}
                      </Box>
                    </Box>
                  }
                />
              </ListItem>
            </Card>
          ))}
        </List>
      )}
    </Box>
  );

  const renderUploadReceipts = () => (
    <Box>
      <AppBar position="static" sx={{ mb: 2 }}>
        <Toolbar>
          <UploadIcon sx={{ mr: 2 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="div">
              Upload Receipts
            </Typography>
          </Box>
        </Toolbar>
        
        {/* Header Navigation Tabs */}
        <Tabs
          value={currentView}
          onChange={(e, newValue) => setCurrentView(newValue)}
          variant="fullWidth"
          textColor="inherit"
          indicatorColor="secondary"
          sx={{ bgcolor: 'primary.dark' }}
        >
          <Tab label="Items" value="items" />
          <Tab label="Upload" value="upload" />
          <Tab label={`Missing (${missingReceipts.length})`} value="missing" />
          <Tab label="Bank" value="bank" />
        </Tabs>
      </AppBar>

      {report ? (
        <Box sx={{ p: 2 }}>
          <MobileReceiptUploader 
            report={report} 
            onUploadComplete={fetchExpenseData}
          />
        </Box>
      ) : (
        <Card sx={{ m: 2 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading expense report...
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );

  const renderMissingReceipts = () => (
    <Box>
      <AppBar position="static" sx={{ mb: 2 }}>
        <Toolbar>
          <WarningIcon sx={{ mr: 2 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="div">
              Missing Receipts ({missingReceipts.length})
            </Typography>
          </Box>
        </Toolbar>
        
        {/* Header Navigation Tabs */}
        <Tabs
          value={currentView}
          onChange={(e, newValue) => setCurrentView(newValue)}
          variant="fullWidth"
          textColor="inherit"
          indicatorColor="secondary"
          sx={{ bgcolor: 'primary.dark' }}
        >
          <Tab label="Items" value="items" />
          <Tab label="Upload" value="upload" />
          <Tab label={`Missing (${missingReceipts.length})`} value="missing" />
          <Tab label="Bank" value="bank" />
        </Tabs>
      </AppBar>

      {missingReceipts.length === 0 ? (
        <Card sx={{ m: 2 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <WarningIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" color="success.main" gutterBottom>
              All Good!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No missing receipts for this month
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <List>
          {missingReceipts.map((item, index) => (
            <Card key={item?.id || index} sx={{ m: 1, mb: 2 }}>
              <ListItem>
                <ListItemIcon>
                  <WarningIcon color="warning" />
                </ListItemIcon>
                <ListItemText
                  primary={item?.description || 'Missing receipt'}
                  secondary={`$${formatAmount(item?.amount)} • ${formatDate(item?.date)}`}
                />
              </ListItem>
            </Card>
          ))}
        </List>
      )}
    </Box>
  );

  const renderBankStatement = () => (
    <Box>
      <AppBar position="static" sx={{ mb: 2 }}>
        <Toolbar>
          <BankIcon sx={{ mr: 2 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="div">
              Bank Statement
            </Typography>
          </Box>
        </Toolbar>
        
        {/* Header Navigation Tabs */}
        <Tabs
          value={currentView}
          onChange={(e, newValue) => setCurrentView(newValue)}
          variant="fullWidth"
          textColor="inherit"
          indicatorColor="secondary"
          sx={{ bgcolor: 'primary.dark' }}
        >
          <Tab label="Items" value="items" />
          <Tab label="Upload" value="upload" />
          <Tab label={`Missing (${missingReceipts.length})`} value="missing" />
          <Tab label="Bank" value="bank" />
        </Tabs>
      </AppBar>

      {bankStatement ? (
        <Card sx={{ m: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Bank Statement Available
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Statement Date: {formatDate(bankStatement?.statement_date)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Bank statement viewer component will be loaded here
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Card sx={{ m: 2 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <BankIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              No bank statement available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Bank statement will appear here when uploaded
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );

  const renderCurrentView = () => {
    switch (currentView) {
      case 'upload':
        return renderUploadReceipts();
      case 'missing':
        return renderMissingReceipts();
      case 'bank':
        return renderBankStatement();
      default:
        return renderExpenseItems();
    }
  };

  return (
    <Box sx={{ pb: 2 }}>
      {renderCurrentView()}
      
      {/* Optional: Speed Dial for quick access (alternative to header tabs) */}
      {currentView === 'items' && (
        <SpeedDial
          ariaLabel="Expense Actions"
          sx={{ position: 'fixed', bottom: 80, right: 16 }}
          icon={<MenuIcon />}
          open={speedDialOpen}
          onOpen={() => setSpeedDialOpen(true)}
          onClose={() => setSpeedDialOpen(false)}
        >
          {speedDialActions.map((action) => (
            <SpeedDialAction
              key={action.key}
              icon={action.icon}
              tooltipTitle={action.name}
              onClick={() => {
                setCurrentView(action.key);
                setSpeedDialOpen(false);
              }}
            />
          ))}
        </SpeedDial>
      )}
    </Box>
  );
};

export default MobileExpenseReportForm; 